import React, { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Landing from '@/pages/Landing';
import Home from '@/pages/Home';
import Auth from '@/pages/Auth';
import Profile from '@/pages/Profile';
import BarDetail from '@/pages/BarDetail';
import Friends from '@/pages/Friends';
import BarOwnerAuth from '@/pages/BarOwnerAuth';
import BarOwnerDashboard from '@/pages/BarOwnerDashboard';
import Leaderboard from '@/pages/Leaderboard';
import SocialFeed from '@/pages/SocialFeed';
import Premium from '@/pages/Premium';
import PremiumSuccess from '@/pages/PremiumSuccess';
import Reservations from '@/pages/Reservations';
import Matchmaking from '@/pages/Matchmaking';
import BarCrawl from '@/pages/BarCrawl';
import SinglesNight from '@/pages/SinglesNight';
import TermsOfService from '@/pages/TermsOfService';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import AdminDashboard from '@/pages/AdminDashboard';
import { Toaster } from '@/components/ui/sonner';
import { initPushNotifications } from '@/utils/native';
import MatrixBackground from '@/components/MatrixBackground';
import MatrixRain from '@/components/MatrixRain';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

import GoogleBarDetail from './pages/GoogleBarDetail';
import GoogleCallback from './pages/GoogleCallback';
import Investors from './pages/Investors';
import JuniorDashboard from './pages/JuniorDashboard';
import YoungBuzzDashboard from './pages/YoungBuzzDashboard';
import AdvertiserAuth from './pages/AdvertiserAuth';
import AdvertiserDashboard from './pages/AdvertiserDashboard';
import MedullaPopupAd from '@/components/MedullaPopupAd';

export const AuthContext = React.createContext();
export const ThemeContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('nightbuzz-dark-mode');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [matrixSkin, setMatrixSkin] = useState(() => {
    const saved = localStorage.getItem('nightbuzz-matrix-skin');
    return saved !== null ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem('nightbuzz-dark-mode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('nightbuzz-matrix-skin', JSON.stringify(matrixSkin));
    if (matrixSkin) {
      document.documentElement.classList.add('matrix-skin');
    } else {
      document.documentElement.classList.remove('matrix-skin');
    }
  }, [matrixSkin]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (e) {
      console.error(e);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    // Fetch fresh user data from server (in case profile pic was uploaded before login)
    setTimeout(() => refreshUser(), 100);
    // Initialize push notifications on native
    initPushNotifications((notification) => {
      console.log('Received push:', notification);
    });
  };

  // Refresh user data from server - call this after profile picture upload
  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      return response.data;
    } catch (e) {
      console.error('Failed to refresh user:', e);
      return null;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <ThemeContext.Provider value={{ darkMode, setDarkMode }}>
      <AuthContext.Provider value={{ user, login, logout, refreshUser }}>
        <div className="App relative min-h-screen">
          {darkMode && <MatrixBackground />}
          <div className="relative z-10">
            <BrowserRouter>
              <Routes>
                <Route path="/" element={user ? <Home /> : <Landing />} />
                <Route path="/auth" element={!user ? <Auth /> : <Navigate to="/home" />} />
                <Route path="/auth/google-callback" element={<GoogleCallback />} />
                <Route path="/home" element={user ? <Home /> : <Navigate to="/" />} />
                <Route path="/profile" element={user ? <Profile /> : <Navigate to="/" />} />
                <Route path="/bar/:barId" element={user ? <BarDetail /> : <Navigate to="/" />} />
                <Route path="/bar/google/:placeId" element={user ? <GoogleBarDetail /> : <Navigate to="/" />} />
                <Route path="/friends" element={user ? <Friends /> : <Navigate to="/" />} />
                <Route path="/bar-owner/auth" element={<BarOwnerAuth />} />
                <Route path="/bar-owner/dashboard" element={<BarOwnerDashboard />} />
                <Route path="/leaderboard" element={user ? <Leaderboard /> : <Navigate to="/" />} />
                <Route path="/feed" element={user ? <SocialFeed /> : <Navigate to="/" />} />
                <Route path="/premium" element={user ? <Premium /> : <Navigate to="/" />} />
                <Route path="/premium/success" element={user ? <PremiumSuccess /> : <Navigate to="/" />} />
                <Route path="/reservations" element={user ? <Reservations /> : <Navigate to="/" />} />
                <Route path="/matchmaking" element={user ? <Matchmaking /> : <Navigate to="/" />} />
                <Route path="/bar-crawl" element={user ? <BarCrawl /> : <Navigate to="/" />} />
                <Route path="/singles-night" element={user ? <SinglesNight /> : <Navigate to="/" />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/investors" element={<Investors />} />
                <Route path="/junior" element={<YoungBuzzDashboard />} />
                <Route path="/junior/dashboard" element={<YoungBuzzDashboard />} />
                <Route path="/youngbuzz" element={<YoungBuzzDashboard />} />
                <Route path="/youngbuzz/dashboard" element={<YoungBuzzDashboard />} />
                <Route path="/advertiser/auth" element={<AdvertiserAuth />} />
                <Route path="/advertiser/dashboard" element={<AdvertiserDashboard />} />
              </Routes>
            </BrowserRouter>
          </div>
          <Toaster />
          <PWAInstallPrompt />
          {user && <MedullaPopupAd />}
        </div>
      </AuthContext.Provider>
    </ThemeContext.Provider>
  );
}

export default App;